import type {
  ClipMediaMetadata,
  EditorialClipBinding,
  MediaFamily,
  MediaMember,
  MediaUnionClipLike,
} from "@/components/OmniCine/Timeline/EditorialMediaBinding";
import type {
  MediaDockResolvedContext,
  MediaDockSelectorContext,
  MediaDockSource,
} from "@/components/OmniCine/Docks/shared/mediaDock.types";

export function toDockKind(raw: unknown): "video" | "audio" | "image" | "unknown" {
  const kind = String(raw ?? "").toLowerCase();
  if (kind === "video" || kind === "audio" || kind === "image") return kind;
  return "unknown";
}

export function readClipMediaUrl(clip: unknown): string | null {
  const source = clip as MediaUnionClipLike | null | undefined;
  if (!source) return null;
  return source.mediaUrl ?? source.url ?? source.previewUrl ?? source.src ?? source.media?.url ?? null;
}

export function readTimelineClips(state: unknown): MediaUnionClipLike[] {
  const source = state as {
    tracks?: Array<{ clips?: MediaUnionClipLike[] }>;
    clips?: MediaUnionClipLike[];
    timeline?: { clips?: MediaUnionClipLike[] };
    present?: { timeline?: { clips?: MediaUnionClipLike[] }; clips?: MediaUnionClipLike[] };
    doc?: { timeline?: { clips?: MediaUnionClipLike[] }; clips?: MediaUnionClipLike[] };
  };

  if (Array.isArray(source?.tracks)) {
    const clips = source.tracks.flatMap((track) => (Array.isArray(track?.clips) ? track.clips : []));
    if (clips.length > 0) return clips;
  }

  if (Array.isArray(source?.clips)) return source.clips;
  if (Array.isArray(source?.timeline?.clips)) return source.timeline.clips;
  if (Array.isArray(source?.present?.timeline?.clips)) return source.present.timeline.clips;
  if (Array.isArray(source?.present?.clips)) return source.present.clips;
  if (Array.isArray(source?.doc?.timeline?.clips)) return source.doc.timeline.clips;
  if (Array.isArray(source?.doc?.clips)) return source.doc.clips;

  return [];
}

export function mapSourceChip(source?: MediaDockSource): string | null {
  if (!source) return null;
  if (source === "inlineWitness") return "Inline Witness";
  if (source === "import") return "Import";
  if (source === "mediaPanel") return "Media Panel";
  return "System";
}

function readClipLabel(clip: MediaUnionClipLike | null): string | null {
  if (!clip) return null;
  return clip.title ?? clip.label ?? clip.name ?? clip.fileName ?? clip.filename ?? clip.assetName ?? clip.mediaName ?? null;
}

function readClipMediaId(clip: MediaUnionClipLike | null): string | null {
  if (!clip) return null;
  return clip.mediaId ?? clip.media?.id ?? null;
}

function resolveFamilyByClipId(
  clipId: string,
  metadata: ClipMediaMetadata | null,
  binding: EditorialClipBinding | null,
  families: MediaFamily[],
): MediaFamily | null {
  const familyId = binding?.familyId ?? metadata?.familyId;
  if (familyId) {
    const family = families.find((entry) => entry.id === familyId);
    if (family) return family;
  }

  return families.find((entry) => entry.clipIds.includes(clipId)) ?? null;
}

export function resolveDockContext(args: MediaDockSelectorContext): MediaDockResolvedContext {
  const clipId = args.focusMedia?.clipId ?? args.focusIntent?.clipId ?? args.selectedClipId ?? null;
  const focusedClip = clipId
    ? readTimelineClips(args.state).find((clip) => String(clip.id ?? clip.clipId ?? "") === String(clipId)) ?? null
    : null;
  const metadata = clipId ? args.clipMediaMetadataByClipId[clipId] ?? null : null;
  const binding = clipId ? args.editorialClipBindingByClipId[clipId] ?? null : null;
  const family = clipId ? resolveFamilyByClipId(clipId, metadata, binding, args.mediaFamilies) : null;
  const members = family ? args.mediaMembers.filter((entry) => entry.familyId === family.id) : [];

  return {
    clipId,
    focusedClip,
    focusedClipLabel: readClipLabel(focusedClip),
    focusedClipMediaId: readClipMediaId(focusedClip),
    focusedClipMediaUrl: readClipMediaUrl(focusedClip),
    metadata,
    binding,
    family,
    members,
    sourceChip: mapSourceChip(args.focusMedia?.source),
  };
}